import React, { useContext, useState } from "react";
import { useToast } from "../context/ToastContext";
import { AuthContext } from "../context/AuthContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import { markRecentGoogleSheet } from "../utils/googleSheetRecents";
import LinesheetSourceSelector from "./shared/LinesheetSourceSelector";
import InventorySourceSelector from "./shared/InventorySourceSelector";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";


import ToolInfoModal from "../components/common/ToolInfoModal";

function PurchaseOrderAllSheetsTool() {
  const [linesheetSource, setLinesheetSource] = useState("google");
  const [selectedGoogleSheet, setSelectedGoogleSheet] = useState(null);
  const [linesheet, setLinesheet] = useState(null);
  
  const [inventorySource, setInventorySource] = useState("upload");
  const [inventoryShopifyConfig, setInventoryShopifyConfig] = useState({
    location: "Main Store",
    vendors: "",
    excludeArchive: true,
    excludeRS: true
  });
  const [inventory, setInventory] = useState(null);
  const [fetchingShopify, setFetchingShopify] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const { user } = useContext(AuthContext);
  const [showInfoModal, setShowInfoModal] = useState(false);

  const toolInfo = {
    toolName: "Generate PO (All Sheets)",
    description: "Batch generate purchase orders for ALL sheets in the linesheet file. Requires a single inventory file to match against.",
    steps: [
      "Choose a shared Google Sheet or upload your Linesheet (Excel) containing multiple vendor tabs.",
      "Upload your Inventory file (Excel).",
      "Click 'Generate All POs'.",
      "The tool will iterate through every sheet in the linesheet and create a PO if matching items are found in inventory."
    ],
    templates: [
      { name: "Linesheet Template", link: `${flaskApi.defaults.baseURL}/templates/download/template_linesheet.xlsx` },
      { name: "Inventory Template", link: `${flaskApi.defaults.baseURL}/templates/download/template_inventory.xlsx` }
    ]
  };

  const guideTips = [
    "Use this version when the linesheet has many tabs and you want the tool to process every one.",
    "Shared Google Sheets are best for team-maintained linesheets because they stay available for everyone with access.",
    "If one or two tabs are all you need, the standard Purchase Order tool is usually the faster option.",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasLinesheet =
      linesheetSource === "google"
        ? Boolean(selectedGoogleSheet?.id)
        : Boolean(linesheet);

    if (!hasLinesheet) {
      addToast("Please provide the linesheet.", "error");
      return;
    }
    
    if (inventorySource === "upload" && !inventory) {
      addToast("Please upload an inventory file.", "error");
      return;
    }

    // Check file size (500MB limit)
    const MAX_SIZE = 100 * 1024 * 1024;
    if (linesheetSource === "upload" && linesheet?.size > MAX_SIZE) {
      addToast("Linesheet is too large. Maximum size is 100MB.", "error");
      return;
    }
    if (inventorySource === "upload" && inventory?.size > MAX_SIZE) {
      addToast("Inventory file is too large. Maximum size is 100MB.", "error");
      return;
    }
    
    let finalInventoryFile = inventory;
    
    // Fetch shopify excel if needed
    if (inventorySource === "shopify") {
      setFetchingShopify(true);
      try {
        const excludeTags = [];
        if (inventoryShopifyConfig.excludeArchive) excludeTags.push("Archive");
        if (inventoryShopifyConfig.excludeRS) excludeTags.push("RS");
        
        const vendors = inventoryShopifyConfig.vendors
          ? inventoryShopifyConfig.vendors.split(",").map(v => v.trim()).filter(v => v)
          : [];
          
        const locationNames = inventoryShopifyConfig.location === "All" 
          ? [] 
          : [inventoryShopifyConfig.location];

        const shopifyRes = await flaskApi.post(
          "/shopify/fetch-inventory-excel", 
          { vendors, exclude_tags: excludeTags, location_names: locationNames },
          { responseType: "blob" }
        );
        
        finalInventoryFile = new File([shopifyRes.data], "shopify_inventory.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });
        
      } catch (err) {
        setFetchingShopify(false);
        const errorMessage = await extractErrorMessage(err, "Failed to fetch inventory from Shopify.");
        addToast(errorMessage, "error");
        return;
      }
      setFetchingShopify(false);
    }

    const formData = new FormData();
    if (linesheetSource === "google") {
      formData.append("google_linesheet_file_id", selectedGoogleSheet.id);
      formData.append("google_linesheet_file_name", selectedGoogleSheet.name);
    } else {
      formData.append("linesheet", linesheet);
    }
    formData.append("inventory", finalInventoryFile);

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post(
        "/purchase/generate-purchase-order-all-sheets",
        formData,
        {
          responseType: "blob",
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );


      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      if (
        response.data.type === "text/html" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("text/html"))
      ) {
        const text = await response.data.text();
        addToast("Server Error: Received HTML instead of file.", "error");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "Purchase_Order_All_Sheets.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      if (linesheetSource === "google" && selectedGoogleSheet?.id) {
        markRecentGoogleSheet(user?.employeeId, selectedGoogleSheet);
      }

      addToast("File downloaded successfully.", "success");
    } catch (err) {
      const errorMessage = await extractErrorMessage(err, "Error generating purchase order.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <ToolPageLayout
        title="PO (All Sheets)"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
        infoLabel="Tool Info"
      >
        <form onSubmit={handleSubmit} className="tool-form tool-form-split">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Inventory Source</h3>
              </div>
              <InventorySourceSelector
                source={inventorySource}
                onSourceChange={setInventorySource}
                uploadFile={inventory}
                onUploadFileChange={setInventory}
                shopifyConfig={inventoryShopifyConfig}
                onShopifyConfigChange={setInventoryShopifyConfig}
              />
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={loading || fetchingShopify}
              >
                {loading || fetchingShopify ? "Processing..." : "Generate All POs"}
              </button>

              <ToolProgressPanel
                loading={loading}
                isDownloadingShopify={fetchingShopify}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading workbook and inventory for processing..."
                processingMessage="Processing Purchase Orders... This may take a while."
              />
            </div>
          </div>

          <div className="tool-form-side">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Linesheet Source</h3>
              </div>
              <LinesheetSourceSelector
                source={linesheetSource}
                onSourceChange={setLinesheetSource}
                uploadFile={linesheet}
                onUploadFileChange={setLinesheet}
                selectedGoogleSheet={selectedGoogleSheet}
                onSelectedGoogleSheetChange={setSelectedGoogleSheet}
                uploadLabel="Linesheet"
              />
            </section>
          </div>
        </form>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}

export default PurchaseOrderAllSheetsTool;
